import { useState, useCallback, useEffect } from 'react';

interface PiPOptions {
  width?: number;
  height?: number;
}

export function usePictureInPicture(options: PiPOptions = {}) {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [isSupported] = useState(() => 'documentPictureInPicture' in window);

  const open = useCallback(async () => {
    if (!isSupported) return null;
    try {
      const pip: Window = await window.documentPictureInPicture.requestWindow({
        width: options.width || 264,
        height: options.height || 186,
        // Bypass Chrome's PictureInPictureBoundsCache — forces fresh window sizing
        // every time instead of reusing cached outer bounds from previous session.
        // Without this, cached bounds compound: each cycle can drift by a few px.
        preferInitialWindowPlacement: true,
      });

      // Inject CSS reset via <style> tag — enters CSSOM before first layout,
      // overriding UA body { margin: 8px } before Chrome computes content size.
      const resetStyle = pip.document.createElement('style');
      resetStyle.textContent = 'html,body{margin:0;padding:0;width:100%;height:100%;overflow:hidden;background:#0e0e1a}';
      pip.document.head.appendChild(resetStyle);
      pip.document.body.style.fontFamily = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

      console.log(`[PiP] requested=${options.width || 264}x${options.height || 186}, actual content=${pip.innerWidth}x${pip.innerHeight}`);

      // No stylesheet copying needed — emotion CacheProvider injects directly into PiP head

      // Sync close state when PiP window closes
      pip.addEventListener('pagehide', () => {
        setPipWindow(null);
      });

      setPipWindow(pip);
      return pip;
    } catch (err) {
      console.error('Failed to open PiP:', err);
      return null;
    }
  }, [isSupported, options.width, options.height]);

  const close = useCallback(() => {
    pipWindow?.close();
    setPipWindow(null);
  }, [pipWindow]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      pipWindow?.close();
    };
  }, [pipWindow]);

  return {
    isSupported,
    isOpen: !!pipWindow,
    pipWindow,
    open,
    close,
  };
}
